import { useMemo } from 'react';

import { UTCDate } from '@date-fns/utc';
import {
  useDirectoryGetProductionCalendar,
  useOrderGetById,
  useOrderGetNewSchema,
} from 'api/requests';
import { convertDateByTimezone, lodash, toZeroTime } from 'helpers';
import { useNavigate } from 'libs/navigation';

import { useDeleteOrderAction } from './useDeleteOrderAction';
import { useDownloadOrder } from './useDownloadOrder';
import { useOrderActions } from './useOrderActions';
import { useUpdateOrder } from './useUpdateOrder';

export const useOrder = () => {
  const { params } = useNavigate<{ orderId: string }>();

  const { order, serverError } = useOrderGetById(params.orderId!);

  const { actions } = useOrderActions(order);

  const { orderSchema, loading: orderSchemaLoading } = useOrderGetNewSchema();

  const { productionCalendar, loading: loadingProductionCalendar } =
    useDirectoryGetProductionCalendar();

  const { onDisableEdit, onEnableEdit, onSubmit, submitLoading } =
    useUpdateOrder(order);

  const {
    loadingDelete,
    isOpenDialogConfirmDelete,
    onCloseDialogConfirmDelete,
    onOpenDialogConfirmDelete,
    onDelete,
  } = useDeleteOrderAction(order);

  const { onDownloadOrder } = useDownloadOrder(order);

  const minEndDate = useMemo(() => {
    if (!orderSchema?.minEndDate) return undefined;
    return toZeroTime(
      convertDateByTimezone(new UTCDate(orderSchema.minEndDate), 18000)
    );
  }, [orderSchema?.minEndDate]);

  const disabledDates = useMemo(
    () =>
      lodash
        .compact(productionCalendar?.nonBusinessDays)
        .map((date) => convertDateByTimezone(new UTCDate(date), 18000)),
    [productionCalendar?.nonBusinessDays]
  );

  // GMT+5 (Алматы)
  // TODO: Feature request to get broker timezone offset from API
  const brokerTimezoneOffset = 18000;

  return {
    loading: orderSchemaLoading || loadingProductionCalendar,
    notFound: !!serverError,
    order,
    actions,
    brokerTimezoneOffset,
    orderSchema,
    minEndDate,
    disabledDates,
    submitLoading,
    loadingDelete,
    isOpenDialogConfirmDelete,
    onEnableEdit,
    onDisableEdit,
    onSubmit,
    onCloseDialogConfirmDelete,
    onOpenDialogConfirmDelete,
    onDelete,
    onDownloadOrder,
  };
};
